// Context compaction for the chat agent's LLM view. Pure and browser-free
// so it can be unit-tested without an Agent.
//
// piChat.js installs `compactMessages` as pi-agent-core's transformContext,
// which runs before EVERY LLM call inside a run - so it must be safe to
// apply to a turn that is still in progress. The agent's own state.messages
// is never mutated here; only the projection sent to the provider changes.
//
// Rules:
//   1. Trigger on estimated tokens against the model's window, never on
//      message count. A twenty-tool-call investigation is normal, not a
//      reason to fold anything. The estimate is held to the provider's own
//      count wherever a reply carries one (promptTokenScale).
//   2. The verbatim tail starts at a turn boundary when one fits the keep
//      budget. When even the current turn is too large, large tool results
//      in the tail are stubbed oldest-first (never one the model has not
//      read yet, nor the newest it is working from), and as a last resort the cut
//      moves inside the turn to an assistant message so a tool call never
//      loses its result.
//   3. The digest keeps every user message verbatim and names every tool
//      call by tool_call_id, so from_tool_calls / expand_preview still reach
//      rows whose inline result was folded.

// Absolute ceiling on the message budget, shared with piChat's byte caps.
import { getCurrentSystemMessage } from "@earendil-works/pi-ai";

export const TOKEN_BUDGET = 700_000;
export const CHARS_PER_TOKEN = 4;
// Fraction of the usable window the conversation may fill before compaction.
const TRIGGER_FRACTION = 0.8;
// Fraction of the trigger budget kept verbatim after a compaction.
const KEEP_FRACTION = 0.5;
// Fraction of the trigger budget the digest itself may occupy.
const SUMMARY_FRACTION = 0.25;
// Tool results at or above the large gate are stubbed first when the tail
// alone is over budget; the small gate is the second pass.
const LARGE_TOOL_RESULT_TOKENS = 8_000;
const SMALL_TOOL_RESULT_TOKENS = 1_000;
// Share of the keep budget held back for the newest results the model has
// already read and is still working from.
const RECENT_RESULTS_FRACTION = 0.5;
// Digest clip lengths in characters. Halved repeatedly when the digest is
// over its own budget. User text is never clipped below MIN_USER_CLIP so the
// question survives even a very long conversation.
const CLIPS = { user: 8_000, notification: 600, assistant: 600, toolArgs: 300, toolResult: 400 };
const MIN_USER_CLIP = 2_000;
const MIN_CLIP = 80;

// ── token estimation ─────────────────────────────────────────────

function estimateContentTokens(parts) {
    if (typeof parts === "string") return Math.ceil(parts.length / 4);
    if (!Array.isArray(parts)) return 0;
    let n = 0;
    for (const c of parts) {
        if (!c) continue;
        if (c.type === "text" && c.text) n += Math.ceil(c.text.length / 4);
        // pi-ai keeps reasoning under `thinking`, and replays it to providers
        // that stream it back, where it counts like any other text.
        else if (c.type === "thinking" && (c.thinking || c.text)) n += Math.ceil(String(c.thinking || c.text).length / 4);
        else if (c.type === "toolCall") {
            // pi-ai stores tool-call args under `arguments`; staged/SDK
            // shapes use `input`/`args`.
            const args = c.arguments ?? c.input ?? c.args ?? {};
            try { n += Math.ceil(JSON.stringify(args).length / 4); } catch { n += 16; }
            if (c.name) n += Math.ceil(c.name.length / 4);
        } else if (c.type === "image") {
            // Vision payloads are billed roughly per-tile by most providers;
            // 800 is a conservative average.
            n += 800;
        }
    }
    return n;
}

export function estimateMessageTokens(m) {
    if (!m) return 0;
    if (m.role === "notification") return m.text ? Math.ceil(m.text.length / 4) : 0;
    if (m.role === "toolResult" || m.role === "assistant" || m.role === "user") {
        return estimateContentTokens(m.content);
    }
    return 0;
}

export function estimateTokens(messages) {
    let n = 0;
    for (const m of messages || []) n += estimateMessageTokens(m);
    return n;
}

// How many tokens the provider counted for each one the estimate sees. A
// reply's usage is the size of the request that produced it, so each reply
// measures the estimate against everything before it. Ids, JSON and
// replayed reasoning run nearer three characters a token than four, so a
// tool-heavy conversation can fill its window while the estimate is still
// under the trigger. The largest ratio is kept, because a request that was
// already compacted is smaller than the history it came from and would
// understate it. Never below 1, so without usage the estimate triggers as
// it always has.
const MAX_TOKEN_SCALE = 3;
export function promptTokenScale(messages, overheadTokens = 0) {
    let scale = 1;
    let before = Math.max(0, Number(overheadTokens) || 0);
    for (const m of messages || []) {
        if (m?.role === "assistant" && m.usage && before > 0) {
            const u = m.usage;
            const reported = (Number(u.input) || 0) + (Number(u.cacheRead) || 0) + (Number(u.cacheWrite) || 0);
            if (reported > 0) scale = Math.max(scale, reported / before);
        }
        before += estimateMessageTokens(m);
    }
    return Math.min(MAX_TOKEN_SCALE, scale);
}

// ── budget ───────────────────────────────────────────────────────

// `contextWindow` is the model's window in tokens; `overheadTokens` is the
// fixed per-request cost the messages must leave room for (system prompt +
// tool schemas); `tokenScale` is promptTokenScale's ratio, which turns the
// budget into the estimate's units. Without a window the budget is the
// absolute cap, so a caller that cannot know the model (the audit "llm
// view") never shows a compaction the live agent would not have performed.
export function compactionBudget({ contextWindow = null, overheadTokens = 0, tokenScale = 1 } = {}) {
    const usable = Math.min(Number(contextWindow) || TOKEN_BUDGET, TOKEN_BUDGET);
    const overhead = Math.max(0, Number(overheadTokens) || 0);
    const scale = Math.max(1, Number(tokenScale) || 1);
    const trigger = Math.max(4_000, Math.floor((usable * TRIGGER_FRACTION) / scale) - overhead);
    return { usable, trigger, keep: Math.floor(trigger * KEEP_FRACTION) };
}

// ── helpers ──────────────────────────────────────────────────────

function extractText(content) {
    if (typeof content === "string") return content;
    if (Array.isArray(content)) {
        return content.filter((c) => c?.type === "text").map((c) => c.text).join(" ");
    }
    return "";
}

function clip(text, max) {
    const s = String(text ?? "");
    if (s.length <= max) return s;
    return `${s.slice(0, max)}... [+${s.length - max} chars]`;
}

function isPromptRole(role) {
    return role === "user" || role === "notification";
}

// A turn starts at the first message of a run of user/notification messages
// (ChatConsole sends [notification, user] pairs ahead of each prompt).
function turnStarts(messages) {
    const out = [];
    for (let i = 0; i < messages.length; i++) {
        if (!isPromptRole(messages[i]?.role)) continue;
        if (i === 0 || !isPromptRole(messages[i - 1]?.role)) out.push(i);
    }
    return out;
}

// Newest turn start whose suffix still fits `keepTokens`; null when even the
// newest turn alone does not fit. Returns 0 when the whole array fits.
function turnCutFor(messages, starts, keepTokens) {
    let suffix = 0;
    let end = messages.length;
    let chosen = null;
    for (let k = starts.length - 1; k >= 0; k--) {
        const s = starts[k];
        suffix += estimateTokens(messages.slice(s, end));
        end = s;
        if (suffix > keepTokens) break;
        chosen = s;
    }
    return chosen;
}

// Newest assistant message after `start` whose suffix fits `keepTokens`.
// Cutting at an assistant message keeps its tool calls adjacent to their
// results; the results of the previous assistant message end the prefix.
function innerCutFor(messages, start, keepTokens) {
    let suffix = 0;
    let end = messages.length;
    let chosen = null;
    for (let i = messages.length - 1; i > start; i--) {
        if (messages[i]?.role !== "assistant") continue;
        suffix += estimateTokens(messages.slice(i, end));
        end = i;
        if (suffix > keepTokens) break;
        chosen = i;
    }
    return chosen;
}

function isStub(m) {
    const first = Array.isArray(m.content) ? m.content[0] : null;
    return first?.type === "text" && typeof first.text === "string" && first.text.length < 200
        && Array.isArray(m.content) && m.content.length === 1;
}

function stubToolResult(tr) {
    const tokens = estimateMessageTokens(tr);
    const id = tr.toolCallId || "?";
    const note = tr.synthesized
        ? `[Staged ${tr.toolName || "tool"} result truncated to save context - full rows still available via tool_call_id ${id} (use execute_python.from_tool_calls or expand_preview)]`
        : `[Earlier ${tr.toolName || "tool"} result truncated to save context - was ~${tokens.toLocaleString()} tokens; if it was row-shaped, the rows are still stored under tool_call_id ${id} (expand_preview / execute_python.from_tool_calls)]`;
    return { ...tr, content: [{ type: "text", text: note }], details: undefined };
}

// The first index stubbing may not touch. Results after the last assistant
// message have not been read yet; stubbed, the model never sees them and
// asks again. The newest results before it are kept too, newest first,
// while they fit RECENT_RESULTS_FRACTION of the budget: an agent placing a
// listing in batches works from it for several calls after it arrives.
function protectedFrom(messages, budgetTokens) {
    let last = messages.length - 1;
    while (last >= 0 && messages[last]?.role !== "assistant") last -= 1;
    let from = last + 1;
    let kept = 0;
    for (let i = last - 1; i >= 0; i--) {
        const m = messages[i];
        if (m?.role !== "toolResult" || isStub(m)) continue;
        kept += estimateMessageTokens(m);
        if (kept > budgetTokens * RECENT_RESULTS_FRACTION) break;
        from = i;
    }
    return from;
}

// Stub oldest-first until the tail fits `budgetTokens`: first results at or
// above the large gate (synthesized staged inputs are always eligible - the
// parent's store still has them), then anything above the small gate, never
// a result protectedFrom holds back. What is left may stay over the budget.
function stubLargeResults(recent, budgetTokens) {
    let out = recent;
    const from = protectedFrom(recent, budgetTokens);
    for (const gate of [LARGE_TOOL_RESULT_TOKENS, SMALL_TOOL_RESULT_TOKENS]) {
        let guard = out.length;
        while (estimateTokens(out) > budgetTokens && guard-- > 0) {
            const i = out.findIndex((m, j) =>
                m?.role === "toolResult"
                && !isStub(m)
                && (m.synthesized === true || (j < from && estimateMessageTokens(m) >= gate)));
            if (i < 0) break;
            out = [...out.slice(0, i), stubToolResult(out[i]), ...out.slice(i + 1)];
        }
        if (estimateTokens(out) <= budgetTokens) break;
    }
    return out;
}

// ── digest ───────────────────────────────────────────────────────

function digestLines(older, clips) {
    const lines = [];
    for (const m of older) {
        if (!m) continue;
        if (m.role === "user") {
            const text = extractText(m.content).trim();
            if (text) lines.push(`user: ${clip(text, clips.user)}`);
            const images = Array.isArray(m.content) ? m.content.filter((c) => c?.type === "image").length : 0;
            if (images) lines.push(`user attached ${images} image${images === 1 ? "" : "s"} (not retained)`);
        } else if (m.role === "notification") {
            if (m.text) lines.push(`context: ${clip(m.text, clips.notification)}`);
        } else if (m.role === "assistant") {
            for (const c of Array.isArray(m.content) ? m.content : []) {
                if (c?.type === "text" && c.text) {
                    lines.push(`agent: ${clip(c.text, clips.assistant)}`);
                } else if (c?.type === "toolCall") {
                    let args = "";
                    try { args = JSON.stringify(c.arguments ?? c.input ?? c.args ?? {}); } catch { args = "{}"; }
                    lines.push(`agent called ${c.name || "tool"} [tool_call_id=${c.id || "?"}] ${clip(args, clips.toolArgs)}`);
                }
            }
        } else if (m.role === "toolResult") {
            const text = extractText(m.content).trim();
            const tag = m.isError ? " ERROR" : "";
            lines.push(`result [tool_call_id=${m.toolCallId || "?"}]${tag}: ${clips.toolResult > 0 ? clip(text, clips.toolResult) : "(omitted)"}`);
        }
    }
    return lines;
}

function buildDigestMessage(older, clips) {
    const folded = estimateTokens(older);
    const body = digestLines(older, clips).join("\n");
    const text = [
        "<earlier_conversation_summary>",
        `Auto-compacted: ${older.length} earlier messages (~${folded.toLocaleString()} tokens) folded into this digest. ` +
            "User messages are verbatim. Tool results are abbreviated, but row-shaped results (analytics_query, execute_python) " +
            "remain stored under the tool_call_id shown and can be reached with expand_preview or execute_python.from_tool_calls. " +
            "Continue the task in progress; this is not a new user request.",
        body,
        "</earlier_conversation_summary>",
    ].join("\n");
    return { role: "user", content: text, timestamp: older[0]?.timestamp ?? Date.now() };
}

function digestFor(older, summaryBudgetTokens) {
    let clips = { ...CLIPS };
    let msg = buildDigestMessage(older, clips);
    for (let i = 0; i < 8 && estimateMessageTokens(msg) > summaryBudgetTokens; i++) {
        clips = {
            user: Math.max(MIN_USER_CLIP, Math.floor(clips.user / 2)),
            notification: Math.max(MIN_CLIP, Math.floor(clips.notification / 2)),
            assistant: Math.max(MIN_CLIP, Math.floor(clips.assistant / 2)),
            toolArgs: Math.max(MIN_CLIP, Math.floor(clips.toolArgs / 2)),
            // Result heads go first: they are evidence the model can re-fetch.
            toolResult: clips.toolResult <= MIN_CLIP ? 0 : Math.max(MIN_CLIP, Math.floor(clips.toolResult / 2)),
        };
        msg = buildDigestMessage(older, clips);
    }
    return msg;
}

// ── entry point ──────────────────────────────────────────────────

// Returns the input array itself when nothing needs to change, so callers
// can detect a no-op by identity.
//
// The prompt and the tools declared to the model are system messages in the
// transcript (pi-agent-core 0.86+). They are not conversation: they are set
// aside before the cut and lead the result as one message, the later ones
// folded in, so a compacted request still carries the prompt and every tool.
export function compactMessages(messages, options = {}) {
    if (!Array.isArray(messages) || !messages.some((m) => m?.role === "system")) return compactConversation(messages, options);
    const conversation = messages.filter((m) => m?.role !== "system");
    const compacted = compactConversation(conversation, options);
    if (compacted === conversation) return messages;
    const head = getCurrentSystemMessage(messages);
    return head ? [head, ...compacted] : compacted;
}

function compactConversation(messages, { force = false, contextWindow = null, overheadTokens = 0 } = {}) {
    if (!Array.isArray(messages) || messages.length <= 2) return messages;
    // Only against a known window: the window-less audit view must never
    // fold earlier than the live agent it stands for.
    const tokenScale = contextWindow ? promptTokenScale(messages, overheadTokens) : 1;
    const { trigger, keep } = compactionBudget({ contextWindow, overheadTokens, tokenScale });
    const total = estimateTokens(messages);
    if (!force && total <= trigger) return messages;

    const starts = turnStarts(messages);
    if (starts.length === 0) return messages;

    let cut;
    if (force && total <= trigger) {
        // Manual /compact under budget: fold every turn but the newest.
        if (starts.length < 2) return messages;
        cut = starts[starts.length - 1];
    } else {
        const fit = turnCutFor(messages, starts, keep);
        // `fit === 0` cannot happen when total > trigger >= keep; guard anyway.
        cut = fit == null ? starts[starts.length - 1] : fit;
    }

    let recent = messages.slice(cut);
    if (estimateTokens(recent) > trigger) {
        recent = stubLargeResults(recent, keep);
        if (estimateTokens(recent) > trigger) {
            const inner = innerCutFor(messages, cut, keep);
            if (inner != null) {
                cut = inner;
                recent = stubLargeResults(messages.slice(inner), keep);
            }
        }
    }

    if (cut <= 0) {
        // Nothing older to fold; the only change (if any) is stubbing.
        return recent.length === messages.length && recent.every((m, i) => m === messages[i]) ? messages : recent;
    }

    const older = messages.slice(0, cut);
    return [digestFor(older, Math.floor(trigger * SUMMARY_FRACTION)), ...recent];
}

// What the compactor needs from a live Agent: the model's window and an
// estimate of the fixed per-request overhead (system prompt + tool schemas)
// the message budget must leave room for. Read live so a mid-chat model
// switch retunes the trigger on the next call. A tool whose schema cannot be
// serialized is skipped, leaving the estimate short rather than throwing.
export function compactionOptionsFor(agent) {
    const state = agent?.state;
    let overheadChars = (state?.systemPrompt || "").length;
    for (const t of state?.tools || []) {
        try {
            overheadChars += JSON.stringify({ name: t?.name, description: t?.description, parameters: t?.parameters })?.length || 0;
        } catch { /* unserializable schema: leave the estimate short */ }
    }
    return {
        contextWindow: state?.model?.contextWindow ?? null,
        overheadTokens: Math.ceil(overheadChars / CHARS_PER_TOKEN),
    };
}
